import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import { Box, Typography } from "@mui/material";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import VideocamOffOutlinedIcon from "@mui/icons-material/VideocamOffOutlined";
import { BRAND } from "../styles/authTheme";

const CameraCapture = forwardRef(function CameraCapture({ onError }, ref) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraError, setCameraError] = useState(null);

  useEffect(() => {
    let active = true;

    async function startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user" },
          audio: false,
        });

        if (!active) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setCameraError(null);
      } catch (err) {
        const message =
          err.name === "NotAllowedError"
            ? "Camera permission denied. You can still punch without a photo."
            : "Unable to access camera.";
        setCameraError(message);
        onError?.(message);
      }
    }

    if (navigator.mediaDevices?.getUserMedia) {
      startCamera();
    } else {
      const message = "Camera not supported on this device.";
      setCameraError(message);
      onError?.(message);
    }

    return () => {
      active = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, [onError]);

  useImperativeHandle(ref, () => ({
    capturePhoto: () =>
      new Promise((resolve) => {
        const video = videoRef.current;
        if (!video || !streamRef.current || cameraError) {
          resolve(null);
          return;
        }

        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 480;
        const ctx = canvas.getContext("2d");
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.85);
      }),
  }));

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: { xs: 0.75, sm: 1.25 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <VideocamOutlinedIcon sx={{ color: BRAND.navy, fontSize: { xs: 18, sm: 20 } }} />
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: BRAND.navy, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
          >
            Camera
          </Typography>
        </Box>
        {!cameraError && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: "#EF4444",
                animation: "blink 1.4s ease-in-out infinite",
                "@keyframes blink": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.35 },
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: BRAND.muted, fontWeight: 600, fontSize: "0.65rem" }}
            >
              LIVE
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          width: "100%",
          borderRadius: { xs: "12px", sm: "16px" },
          overflow: "hidden",
          bgcolor: "#0F172A",
          aspectRatio: { xs: "16 / 10", sm: "4 / 3" },
          position: "relative",
          border: `2px solid ${BRAND.border}`,
          boxShadow: { xs: "0 4px 16px rgba(26, 43, 74, 0.1)", sm: "0 8px 32px rgba(26, 43, 74, 0.12)" },
        }}
      >
        {cameraError ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              p: 3,
              textAlign: "center",
            }}
          >
            <VideocamOffOutlinedIcon sx={{ fontSize: 52, mb: 1.5, opacity: 0.6 }} />
            <Typography variant="body2" sx={{ maxWidth: 260, lineHeight: 1.6 }}>
              {cameraError}
            </Typography>
          </Box>
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transform: "scaleX(-1)",
              }}
            />

            {/* Face guide overlay */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  width: "55%",
                  height: "70%",
                  borderRadius: "50%",
                  border: "2px dashed rgba(255,255,255,0.45)",
                  boxShadow: "inset 0 0 40px rgba(0,0,0,0.15)",
                }}
              />
            </Box>

            {/* Corner brackets */}
            {["top-left", "top-right", "bottom-left", "bottom-right"].map((corner) => {
              const styles = {
                position: "absolute",
                width: { xs: 16, sm: 24 },
                height: { xs: 16, sm: 24 },
                borderColor: BRAND.orange,
                borderStyle: "solid",
              };
              if (corner.includes("top")) styles.top = { xs: 10, sm: 16 };
              if (corner.includes("bottom")) styles.bottom = { xs: 10, sm: 16 };
              if (corner.includes("left")) {
                styles.left = { xs: 10, sm: 16 };
                styles.borderWidth = "2px 0 0 2px";
              } else {
                styles.right = { xs: 10, sm: 16 };
                styles.borderWidth = "2px 2px 0 0";
              }
              if (corner.includes("bottom-left")) styles.borderWidth = "0 0 2px 2px";
              if (corner.includes("bottom-right")) styles.borderWidth = "0 2px 2px 0";
              return <Box key={corner} sx={styles} />;
            })}
          </>
        )}

      </Box>
    </Box>
  );
});

export default CameraCapture;
